<template>
    <div
        class="q-pb-lg"
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <!-- Warning banner displayed if cause has shutdown as type of loss -->
        <planned-shutdown-banner :show="form.type_of_loss == 'shutdown'" />
        <q-form @submit="isEdit ? update() : create()" ref="eventForm">
            <table-select
                :modelValue="categorySelect.selected"
                @update:modelValue="categorySelect.selected = $event"
                :label="categorySelect.label"
                :baseURL="categorySelect.url"
                :queryParams="categorySelect.queryParams"
                :dialogTitle="categorySelect.dialogTitle"
                :searchLabel="categorySelect.searchLabel"
                :columns="categorySelect.columns"
                :row-key="'id'"
                :cardMode="false"
                class="q-mb-md col"
            >
                <template v-slot:input>
                    <q-btn
                        @click="
                            $router.push({
                                name: 'category_create'
                            })
                        "
                        icon="add"
                        color="accent"
                        size="md"
                        round
                        dense
                    >
                        <q-tooltip>{{ $t('Add category') }}</q-tooltip>
                    </q-btn>
                </template>
            </table-select>
            <!-- Warning banner for when selected type of loss is not allowed -->
            <q-banner
                v-if="!isValidTypeOfLossOption"
                class="background-yellow border-yellow q-mb-md border-yellow"
                rounded
                inline-actions
            >
                <template v-slot:avatar>
                    <q-icon
                        name="fa-solid fa-triangle-exclamation"
                        color="warning"
                    ></q-icon>
                </template>
                {{
                    $t(
                        'The type of loss has been disabled for this company. You will be able to save the cause but it will not be available for selection in the future.'
                    )
                }}
            </q-banner>
            <q-select
                v-model="form.type_of_loss"
                :options="getFilteredTypeOfLossOptions"
                :label="$t('Type of Loss')"
                class="q-mb-md"
                bg-color="white"
                outlined
                emit-value
                map-options
                color="accent"
                :rules="[
                    val => val !== '' || $t('Please select a type of loss.')
                ]"
            />
            <q-input
                v-model="form.name"
                @clear="form.name = ''"
                :rules="[val => val !== '' || $t('Enter a name')]"
                :label="$t('Name')"
                class="q-mb-md"
                bg-color="white"
                clearable
                outlined
                lazy-rules
                color="accent"
            ></q-input>
            <q-input
                v-model="form.code"
                @clear="form.code = ''"
                :rules="[val => val !== '' || $t('Enter a code')]"
                :label="$t('Code')"
                class="q-mb-md"
                bg-color="white"
                outlined
                clearable
                color="accent"
            ></q-input>
            <q-input
                v-model="form.description"
                @clear="form.description = ''"
                :label="$t('Description')"
                class="q-mb-lg"
                bg-color="white"
                outlined
                clearable
                color="accent"
            />

            <div class="q-mb-lg">
                <q-checkbox
                    v-model="form.equipmentFailure"
                    :label="$t('Equipment failure')"
                    color="accent"
                />
                <q-icon
                    name="fa-regular fa-exclamation-circle"
                    size="xs"
                    color="secondary"
                    class="q-ml-sm"
                >
                    <q-tooltip>
                        {{
                            $t(
                                'If checked, this downtime will be considered an equipment failure and recorded in maintenance reports.'
                            )
                        }}
                    </q-tooltip>
                </q-icon>
            </div>

            <q-checkbox
                v-model="form.planned"
                :label="$t('Planned downtime')"
                :disable="form.type_of_loss === 'shutdown'"
                class="q-mb-lg"
                color="accent"
            />
            <q-input
                v-if="form.planned"
                v-model="form.target_down_time"
                :rules="[validateDowntime]"
                :label="$t('Target downtime (min)')"
                type="number"
                step="any"
                class="q-mb-lg"
                bg-color="white"
                outlined
                clearable
                color="accent"
            />
            <div v-if="isAdminOrManager" class="q-mb-md">
                <div v-if="form.planned" class="q-mb-lg">
                    <b>{{ $t('Advanced settings') }} </b>
                </div>
                <q-banner
                    v-if="form.planned && form.lockTransitionToRunning"
                    class="background-yellow border-yellow q-mt-sm q-mb-md"
                    inline-actions
                    rounded
                >
                    <template v-slot:avatar>
                        <q-icon
                            name="fa-solid fa-triangle-exclamation"
                            color="warning"
                        ></q-icon>
                    </template>
                    {{
                        $t(
                            'If product count is ignored, the system will not automatically restart until the target downtime is reached.'
                        )
                    }}
                </q-banner>
                <q-checkbox
                    v-if="form.planned"
                    v-model="form.lockTransitionToRunning"
                    :label="$t('Ignore product count during changeover')"
                    class="q-mb-lg"
                    color="accent"
                />
            </div>
            <div class="form__actions">
                <div>
                    <q-btn
                        v-if="isEdit"
                        @click="handleRemove"
                        :color="
                            $userCannot('delete', 'causes')
                                ? 'grey'
                                : 'negative'
                        "
                        :disable="$userCannot('delete', 'causes')"
                        class="float-left q-px-xs"
                        icon="fas fa-trash-alt"
                    ></q-btn>
                    <q-tooltip v-if="$userCannot('delete', 'causes')">
                        {{
                            $t(
                                'You do not have permission to perform this action.'
                            )
                        }}
                    </q-tooltip>
                </div>
                <div>
                    <q-btn
                        :label="$t('Save')"
                        :color="
                            $userCannot('create', 'causes') ? 'grey' : 'primary'
                        "
                        :disable="
                            $userCannot('create', 'causes') || !causeIsValid
                        "
                        type="submit"
                        class="float-right"
                        id="save-btn"
                    />
                    <q-tooltip v-if="$userCannot('create', 'causes')">
                        {{
                            $t(
                                'You do not have permission to perform this action.'
                            )
                        }}
                    </q-tooltip>
                </div>
                <q-btn
                    @click="$router.go(-1)"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm bg-white"
                    text-color="primary"
                    color="white"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import PlannedShutdownBanner from '@components/PlannedShutdownBanner.vue';
import TableSelect from '@components/TableSelect.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { TYPES_OF_LOSSES } from '@models/causes.js';
import { mapActions, mapState } from 'pinia';
import { useSettingsStore } from '@stores/settings.js';
import { useSharedStore } from '@stores/shared.js';
import { useCausesStore } from '@stores/causes.js';
import { useAssetsStore } from '@stores/assets.ts';
import { useFacilitiesStore } from '@stores/facilities.ts';

export default {
    name: 'CauseDetail',
    components: { TableSelect, PlannedShutdownBanner },
    mixins: [BreadcrumbsManager],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        },
        causeId: {
            type: [Number, String],
            required: false,
            default: null
        }
    },
    watch: {
        /**
         * Force downtime to be a planned downtime if it's type is planned shutdown.
         */
        'form.type_of_loss'(val) {
            if (val === 'shutdown') {
                this.form.planned = true;
            }
        }
    },
    mounted() {
        // init form
        if (this.isEdit) {
            this.initForm();
        }

        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Downtime Causes'),
                route: {
                    name: 'cause_list',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.isEdit
                    ? history.state.name
                    : this.$t('Create downtime cause')
            }
        ]);
    },
    data() {
        return {
            form: {
                facility: this.facilityId,
                name: '',
                code: '',
                target_down_time: 0,
                original_target_down_time: 0,
                planned: false,
                description: '',
                type_of_loss: '',
                equipmentFailure: false,
                lockTransitionToRunning: false
            },

            // category selector
            categorySelect: {
                selected: [],
                url: `/facilities/${this.facilityId}/categories/`,
                queryParams: { facility: this.facilityId },
                label: this.$t('Category'),
                dialogTitle: this.$t('Select category'),
                searchLabel: this.$t('Search category'),
                columns: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    }
                ]
            }
        };
    },
    computed: {
        ...mapState(useAssetsStore, ['getAssetPhase', 'getAssets']),
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useSettingsStore, ['typeOfLossOptions']),
        /**
         * Get selected facility.
         * @return {Object} - Facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Determine if form is in edit mode.
         * @return {Boolean}
         */
        isEdit() {
            return this.causeId !== null;
        },
        /**
         * Payload ready for server submission.
         * @return {Object}
         */
        payload() {
            const payload = {
                facility: this.form.facility,
                category: this.categorySelect.selected['id'] ?? null,
                type_of_loss: this.form.type_of_loss,
                name: this.form.name,
                code: this.form.code,
                planned: this.form.planned,
                // Sentry reports that this.form.target_down_time is sometimes
                // an empty string, if this is the case, set it to 0
                target_down_time: this.form.target_down_time
                    ? this.form.target_down_time
                    : 0,
                description: this.form.description,
                equipment_failure: this.form.equipmentFailure,
                lock_transition_to_running: this.form.planned
                    ? this.form.lockTransitionToRunning
                    : false
            };
            return payload;
        },
        /**
         * Determine if the target downtime value is being modified
         * @return {Boolean}
         */
        targetDownTimeChanged() {
            return (
                this.form['target_down_time'] !=
                this.form['original_target_down_time']
            );
        },
        /**
         * Get the client's type of loss
         * @return {Array} - Array of type of loss
         */
        clientTypeOfLoss() {
            return this.typeOfLossOptions;
        },
        /**
         * Filter the type of loss options based on the client's type of loss
         * @return {Array} - Array of type of loss options
         */
        getFilteredTypeOfLossOptions() {
            // Filter the default type of loss options based on the client's type of loss
            const filteredTypeOfLoss = TYPES_OF_LOSSES.filter(option =>
                this.clientTypeOfLoss.includes(option.value)
            );
            // If the form is in edit mode, check if the current type of loss is present
            // in the client's type of loss. If not, add it to the filtered type of loss.
            // A warning will be displayed to the user.
            if (this.isEdit) {
                const currentTypeOfLoss = TYPES_OF_LOSSES.find(
                    option => option.value === history.state.type_of_loss
                );
                if (!filteredTypeOfLoss.includes(currentTypeOfLoss)) {
                    filteredTypeOfLoss.push(currentTypeOfLoss);
                }
            }
            // Translate the label if it exists
            return filteredTypeOfLoss.map(option => ({
                ...option,
                label: option?.label ? this.$t(option.label) : ''
            }));
        },
        /**
         * Verify if the selected type of loss is present in the client's type of loss
         * @return {Boolean} - True if it is present or if the the type of loss select is empty
         */
        isValidTypeOfLossOption() {
            if (
                this.form.type_of_loss === '' ||
                this.clientTypeOfLoss.includes(this.form.type_of_loss)
            ) {
                return true;
            }
            return false;
        },
        /**
         * Only administrators and manager are allowed to change advanced settings
         * @return {Boolean} - True if the user has the necessary role
         */
        isAdminOrManager() {
            return this.$isUserAdmin() || this.$isUserManager();
        },
        /**
         * Determine if the cause is valid
         * @return {Boolean} - True if the cause is valid
         */
        causeIsValid() {
            return this.form.name && this.form.code;
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useCausesStore, ['setCauseAction']),
        /**
         * Initialize form.
         */
        initForm() {
            // If the user enter the URL directly, the history state will be empty
            // and the form will not be initialized
            if (!history.state.name || !history.state.code) {
                // Fetch cause details from the API
                this.fetchCauseDetails();
            } else {
                this.populateCauseForm(history.state);
            }
        },
        /**
         * Create cause.
         */
        async create() {
            // begin creating new cause
            this.beginLoading(`${this.$t('Creating new cause')}...`);

            try {
                const response = await this.$axios.post(
                    'causes/',
                    this.payload
                );
                this.setCauseAction(response.data);
                this.$notifySuccess(this.$t('Downtime cause created'));
                this.$router.go(-1);
            } catch (error) {
                this.handleApiErrors(error, 'create');
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        /**
         * Update cause.
         */
        async update() {
            // check if the user is trying to change the target downtime value
            if (this.targetDownTimeChanged) {
                // check if one of the assets is in a changeover phase and is under the current cause
                const assets = this.getAssets(this.facilityId);
                const switchCauseInProgress = assets.some(asset => {
                    const phase = this.getAssetPhase(asset.id);
                    return (
                        phase['status'] === 'changeover' &&
                        phase['cause'] === parseInt(this.causeId)
                    );
                });

                // prevent the user from updating the target downtime value if switch cause is in progress
                if (switchCauseInProgress) {
                    this.$notifyError(
                        this.$t(
                            'Failed to update target downtime value because an asset is in a changeover phase with this cause.'
                        )
                    );
                    return;
                }
            }

            // begin updating cause
            this.beginLoading(`${this.$t('Updating cause')}...`);
            await this.$axios
                .put(`causes/${this.causeId}/`, this.payload)
                .then(response => {
                    this.setCauseAction(response.data);
                    this.$notifySuccess(this.$t('Downtime cause updated'));
                    this.$router.go(-1);
                })
                .catch(error => {
                    this.handleApiErrors(error, 'update');
                })
                .finally(() => {
                    // stop loading
                    this.stopLoading();
                });
        },
        /**
         * Handles API errors by performing the specified action.
         *
         * @param {Object} error - The error object returned from the API.
         * @param {String} action - The action that was being performed when the error occurred.
         */
        handleApiErrors(e, action) {
            const error =
                e?.response?.data && typeof e.response.data === 'object'
                    ? e.response.data
                    : {};
            const apiErrors = {
                unique: 'cause_code_already_exists',
                maxLength: 'Ensure this field has no more than 100 characters.',
                descriptionMaxLength:
                    'Ensure this field has no more than 150 characters.',
                invalidPk: 'Invalid pk'
            };
            // Handle non unique product code error
            if (
                'error_code' in error &&
                Array.isArray(error['error_code']) &&
                error.error_code.includes(apiErrors.unique)
            ) {
                this.$notifyError(
                    this.$t(
                        'A cause with the same code already exists in this facility.'
                    )
                );

                // Handle name length error
            } else if (
                error.name &&
                error.name[0].includes(apiErrors.maxLength)
            ) {
                this.$notifyError(
                    this.$t('Ensure the name has no more than 100 characters.')
                );

                // Handle code length error
            } else if (
                error.code &&
                error.code[0].includes(apiErrors.maxLength)
            ) {
                this.$notifyError(
                    this.$t(
                        'Ensure the cause code has no more than 100 characters.'
                    )
                );

                // Handle description length error
            } else if (
                error.description &&
                error.description[0].includes(apiErrors.descriptionMaxLength)
            ) {
                this.$notifyError(
                    this.$t(
                        'Ensure the description has no more than 150 characters.'
                    )
                );
            } // Handle deleted category error
            else if (
                error.category &&
                error.category[0].includes(apiErrors.invalidPk)
            ) {
                this.$notifyError(
                    this.$t(
                        'The selected category has been deleted. Please select another category.'
                    )
                );
                // Handle other errors
            } else {
                if (action === 'update') {
                    this.$notifyError(
                        this.$t('Failed to update downtime cause.'),
                        error
                    );
                } else {
                    this.$notifyError(
                        this.$t('Failed to create downtime cause.'),
                        error
                    );
                }
            }
        },
        /**
         * Handle remove cause.
         */
        async handleRemove() {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this downtime cause?')
            ).onOk(async () => {
                await this.remove();
            });
        },
        /**
         * Remove cause.
         */
        async remove() {
            // begin updating cause
            this.beginLoading(`${this.$t('Deleting cause')}...`);
            await this.$axios
                .delete(`causes/${this.causeId}`)
                .then(response => {
                    this.$notifySuccess(this.$t('Downtime cause deleted'));
                    this.$router.go(-1);
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to remove downtime cause. Please try again later.'
                        ),
                        error
                    );
                })
                .finally(() => {
                    // stop loading
                    this.stopLoading();
                });
        },

        /**
         * Validate the target downtime input
         * @param {String} value - User input from target downtime field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validateDowntime(value) {
            // convert the input value to float
            value = parseFloat(value);
            if (!isNaN(value)) {
                if (value <= 0.5) {
                    return this.$t(
                        'Planned downtime must be greater than 0.5 minute'
                    );
                }
            } else {
                return this.$t('Enter a real number');
            }
            return true;
        },
        /**
         * Populates the form with the details of a given cause.
         * @param {Object} cause - The cause object containing details to populate the form.
         */
        populateCauseForm(cause) {
            this.form['name'] = cause.name;
            this.form['code'] = cause.code;
            this.form['planned'] = cause.planned;
            this.form['target_down_time'] = cause.target_down_time;
            this.form['original_target_down_time'] = cause.target_down_time;
            this.form['description'] = cause.description;
            this.form['type_of_loss'] = cause.type_of_loss;
            this.form['equipmentFailure'] = cause.equipment_failure ?? false;
            this.form['lockTransitionToRunning'] =
                cause.lock_transition_to_running ?? false;
            // initialize category selector
            this.categorySelect.selected = {
                id: cause.category,
                name: cause.category_name
            };
        },
        /**
         * Fetches the details of a specific cause from the API.
         * @async
         * @returns {Promise<void>} A promise that resolves when the cause details
         *                          have been successfully fetched and processed.
         */
        async fetchCauseDetails() {
            try {
                const response = await this.$axios.get(
                    `causes/${this.causeId}/`
                );

                const causeData = response.data;
                const cause = {
                    name: causeData.name,
                    code: causeData.code,
                    planned: causeData.planned,
                    target_down_time: causeData.target_down_time,
                    original_target_down_time: causeData.target_down_time,
                    description: causeData.description,
                    type_of_loss: causeData.type_of_loss,
                    equipmentFailure: causeData.equipment_failure,
                    lockTransitionToRunning:
                        causeData.lock_transition_to_running,
                    category: causeData.category,
                    category_name: causeData.category_name
                };
                this.populateCauseForm(cause);
            } catch (error) {
                if (
                    error.detail &&
                    error.detail === 'No Cause matches the given query.'
                ) {
                    this.$notifyError(
                        this.$t(
                            'The cause you are looking for does not exist.'
                        ),
                        error
                    );
                } else {
                    this.$notifyError(
                        this.$t('Failed to load cause details.'),
                        error
                    );
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped></style>
